import React, { useState, useMemo } from "react";
import { debounce } from "lodash";

import FluidImage from "@/components/images/fluid-image";
import {
  Control,
  FieldPath,
  FieldValues,
  useController,
} from "react-hook-form";
import { Combobox, ComboboxInput, ComboboxButton } from "@headlessui/react";
import { cn, formatInputValue } from "@/lib/utils";
import { FLUID_CDN_URL } from "@/lib/constants";
import { AddressSuggestion } from "@/components/checkout/hooks/useAutocomplete";

interface FormTextInputProps<
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
> extends Omit<React.InputHTMLAttributes<HTMLInputElement>, "name"> {
  control: Control<TFieldValues>;
  name: TName;
  label: string;
  inputStyles?: string;
  containerStyles?: string;
  renderAutocompleteOptions: (isFocused: boolean) => React.ReactNode;
  getAutocompleteOptions: (input: string, formCountry: string) => void;
  handleOptionClick: (option: AddressSuggestion, formCountry: string) => void;
  formCountry: string;
  autoComplete?: string;
  showErrorMsg?: boolean;
  handleInputChange?: (value: string) => void;
}

export default function FormInputDropdown<
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
>({
  control,
  name,
  label,
  inputStyles,
  containerStyles,
  renderAutocompleteOptions,
  getAutocompleteOptions,
  handleOptionClick,
  formCountry,
  autoComplete,
  showErrorMsg = false,
  handleInputChange,
  ...props
}: FormTextInputProps<TFieldValues, TName>) {
  const {
    field,
    fieldState: { error },
  } = useController({
    name,
    control: control,
  });
  const [isFocused, setIsFocused] = useState(false);

  const debouncedGetAutocomplete = useMemo(
    () =>
      debounce((value: string, country: string) => {
        getAutocompleteOptions(value, country);
      }, 500),
    [getAutocompleteOptions],
  );

  return (
    <div
      className={cn("relative space-y-px rounded bg-white", containerStyles)}
    >
      <Combobox
        as="div"
        className="relative"
        value={field.value || ""}
        onChange={(option: AddressSuggestion | string | null) => {
          if (option && typeof option !== "string") {
            handleOptionClick(option, formCountry);
          }
        }}
      >
        <ComboboxInput
          {...props}
          value={field.value || ""}
          onChange={(e) => {
            let value = e.target.value;
            value = formatInputValue(value, label);
            field.onChange(value);
            handleInputChange?.(value);
            if (value) debouncedGetAutocomplete(value, formCountry);
          }}
          name={name}
          autoComplete={autoComplete}
          className={cn(
            "inputText peer h-input relative z-10 border-0 pt-5 text-base ring-1 focus:z-20 focus:ring-2 focus:ring-black focus:ring-inset sm:text-sm",
            error ? "shadow-error" : "ring-gray-300",
            inputStyles,
          )}
          onFocus={() => setIsFocused(true)}
          onBlur={() => setIsFocused(false)}
        />
        <ComboboxButton className="cursor-text">
          <span
            className={cn(
              "absolute left-3 z-20 -translate-y-1/2 transform transition-all peer-focus:text-gray-700",
              "max-w-[calc(100%-2rem)] truncate",
              error?.message && showErrorMsg ? "text-red-400" : "text-gray-400",
              field.value || isFocused ? "top-1/4 text-xs" : "top-1/2 text-sm",
            )}
            onClick={() => setIsFocused(true)}
          >
            {error?.message && showErrorMsg ? error.message : label}
          </span>
          {error && (
            <FluidImage
              src={`${FLUID_CDN_URL}/images/checkout/icons/circle-exclamation.svg`}
              alt="error"
              width={14}
              height={14}
              className="red-icons absolute top-1/2 right-3 z-20 -translate-y-1/2 transform"
            />
          )}
        </ComboboxButton>
        {renderAutocompleteOptions(isFocused)}
      </Combobox>
    </div>
  );
}
